import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { japanPrefectures } from '../mocks/japanPrefectures';

export interface PrefectureArtist {
  userId: string;
  name: string;
  location: string;
  genre: string;
  image: string;
  color: string;
  platforms: string[];
}

/**
 * 都道府県IDからSupabaseに登録されたアーティストを取得するフック
 * artist_layers.prefecture（日本語名）と japanPrefectures の id をマッピング
 */
export function usePrefectureArtists(prefectureId: string) {
  const [artists, setArtists] = useState<PrefectureArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchArtists = async () => {
      setLoading(true);
      try {
        // prefectureId (例: "nara") → 日本語名 (例: "奈良県") に変換
        const prefData = japanPrefectures.find((p) => p.id === prefectureId);
        if (!prefData) {
          setArtists([]);
          setCount(0);
          setLoading(false);
          return;
        }
        const prefName = prefData.name; // "奈良県"

        // artist_layersから直接都道府県でフィルタリング（prefectureカラムを使用）
        const { data: layers, error: layerError } = await supabase
          .from('artist_layers')
          .select(
            'user_id, artist_name, genre, location, color, profile_image_url, socials, prefecture'
          )
          .eq('prefecture', prefName);

        if (layerError) throw layerError;

        if (!layers || layers.length === 0) {
          setArtists([]);
          setCount(0);
          setLoading(false);
          return;
        }

        // プロフィール情報も取得してmakerタイプを確認
        const userIds = layers.map((l) => l.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, user_type')
          .in('id', userIds)
          .eq('user_type', 'maker');

        if (!profiles || profiles.length === 0) {
          setArtists([]);
          setCount(0);
          setLoading(false);
          return;
        }

        const profileMap = new Map(profiles.map((p) => [p.id, p]));

        // レイヤー情報とプロフィールを結合（makerタイプのみ）
        const result: PrefectureArtist[] = layers
          .filter((layer) => profileMap.has(layer.user_id))
          .map((layer) => {
            const profile = profileMap.get(layer.user_id);

            // SNSからプラットフォーム一覧を抽出
            const platforms: string[] = [];
            if (layer?.socials && Array.isArray(layer.socials)) {
              (layer.socials as { platform: string }[]).forEach((s) => {
                const name = (s.platform || '').toLowerCase();
                if (name.includes('instagram')) platforms.push('instagram');
                else if (name.includes('twitter') || name.includes('x')) platforms.push('twitter');
                else if (name.includes('youtube')) platforms.push('youtube');
                else if (name.includes('soundcloud')) platforms.push('soundcloud');
                else if (name.includes('spotify')) platforms.push('spotify');
                else if (name.includes('tiktok')) platforms.push('tiktok');
              });
            }

            return {
              userId: layer.user_id,
              name: layer.artist_name || profile?.display_name || 'UNKNOWN',
              location: layer.location || prefData.nameEn || '',
              genre: layer.genre || '',
              image: layer.profile_image_url || '',
              color: layer.color || '#00ff41',
              platforms: platforms.slice(0, 3),
            };
          });

        setArtists(result);
        setCount(result.length);
      } catch (err) {
        console.error('Failed to fetch prefecture artists:', err);
        setArtists([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, [prefectureId]);

  return { artists, count, loading };
}
